/**
 * useWizard - manages wizard session lifecycle, SSE streaming, and file uploads.
 *
 * Uses fetch + ReadableStream for SSE consumption (axios does not support streaming).
 * React Query handles session fetching and mutations; streaming state is local.
 */

import { useCallback, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useUploadFile } from '@/admin/hooks/use-products'
import api from '@/admin/lib/api'
import { buildApiUrl } from '@/shared/lib/api-origin'
import { getAuthToken } from '@/shared/lib/auth-token'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'
export interface WizardMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: string
  metadata?: Record<string, unknown>
}

export interface WizardSessionFile {
  uuid: string
  original_filename: string
  file_role: string
  created_at: string
}

export interface WizardSession {
  uuid: string
  wizard_type: string
  status: string
  messages: WizardMessage[]
  proposal: Record<string, unknown> | null
  proposal_version: number
  context_data: Record<string, unknown>
  expires_at: string
  created_at: string
  updated_at: string
  files: WizardSessionFile[]
}

interface SSEEvent {
  type: 'chunk' | 'proposal' | 'done' | 'error'
  text?: string
  data?: Record<string, unknown>
  detail?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getOrgUuid(): string | null {
  return sessionStorage.getItem('activeOrganisationUuid')
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useWizard(wizardType: string) {
  const qc = useQueryClient()
  const uploadFile = useUploadFile()

  // Streaming state (not in React Query - too real-time)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingText, setStreamingText] = useState('')
  const [sessionUuid, setSessionUuid] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // --- Session query ---
  const sessionQuery = useQuery<WizardSession>({
    queryKey: ['wizard-session', sessionUuid],
    queryFn: () => api.get(`/wizards/sessions/${sessionUuid}/`).then(r => r.data),
    enabled: !!sessionUuid,
    refetchOnWindowFocus: false,
  })

  const session = sessionQuery.data ?? null

  // --- Create / resume session ---
  const createSession = useCallback(async () => {
    try {
      const res = await api.post('/wizards/sessions/', { wizard_type: wizardType })
      const data = res.data as WizardSession
      setSessionUuid(data.uuid)
      qc.setQueryData(['wizard-session', data.uuid], data)
      return data
    } catch (err) {
      toastApiError(err, 'Failed to start wizard session.');
      return null
    }
  }, [wizardType, qc])

  // --- Send message (SSE streaming) ---
  const sendMessage = useCallback(async (content: string) => {
    if (!sessionUuid || isStreaming) return

    setIsStreaming(true)
    setStreamingText('')

    // Optimistically add user message to local cache
    qc.setQueryData<WizardSession>(['wizard-session', sessionUuid], (old) => {
      if (!old) return old
      return {
        ...old,
        messages: [
          ...old.messages,
          { role: 'user' as const, content, timestamp: new Date().toISOString() },
        ],
      }
    })

    const abort = new AbortController()
    abortRef.current = abort

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      const token = getAuthToken()
      if (token) headers['X-Session-Token'] = token
      const orgUuid = getOrgUuid()
      if (orgUuid) headers['X-Active-User-Organisation-UUID'] = orgUuid

      const response = await fetch(buildApiUrl(`/api/wizards/sessions/${sessionUuid}/messages/`), {
        method: 'POST',
        headers,
        body: JSON.stringify({ content }),
        credentials: 'include',
        signal: abort.signal,
      })

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Parse SSE events from buffer
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const jsonStr = line.slice(6).trim()
          if (!jsonStr) continue

          try {
            const event: SSEEvent = JSON.parse(jsonStr)

            if (event.type === 'chunk' && event.text) {
              fullText += event.text
              setStreamingText(fullText)
            } else if (event.type === 'proposal' && event.data) {
              // Proposal received - will be picked up on session refresh
            } else if (event.type === 'error' && event.detail) {
              toast.error(event.detail)
            }
          } catch {
            // Skip malformed events
          }
        }
      }

      // Refresh session to get updated messages and proposal
      await qc.invalidateQueries({ queryKey: ['wizard-session', sessionUuid] })
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        toastApiError(err, 'Failed to send message.')
      }
    } finally {
      setIsStreaming(false)
      setStreamingText('')
      abortRef.current = null
    }
  }, [sessionUuid, isStreaming, qc])

  // --- Upload file ---
  const handleUploadFile = useCallback(async (file: File) => {
    if (!sessionUuid) return

    try {
      // 1. Upload via existing file upload endpoint
      const uploaded = await uploadFile.mutateAsync({
        file,
        use_case: 'WIZARD_SPREADSHEET',
      })

      // 2. Attach to wizard session
      await api.post(`/wizards/sessions/${sessionUuid}/files/`, {
        uploaded_file_uuid: uploaded.uuid,
        file_role: 'spreadsheet',
      })

      // 3. Refresh session
      await qc.invalidateQueries({ queryKey: ['wizard-session', sessionUuid] })

      toast.success(`${file.name} uploaded and parsed.`)
    } catch (err) {
      toastApiError(err, 'Failed to upload file.');
    }
  }, [sessionUuid, uploadFile, qc])

  // --- Accept proposal ---
  const acceptProposal = useMutation({
    mutationFn: () => api.post(`/wizards/sessions/${sessionUuid}/accept/`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wizard-session', sessionUuid] })
      // Invalidate product queries so the new data appears
      qc.invalidateQueries({ queryKey: ['product-templates'] })
      qc.invalidateQueries({ queryKey: ['product-categories'] })
      qc.invalidateQueries({ queryKey: ['products'] })
      toast.success('Proposal accepted - products created.')
    },
    onError: (err) => toastApiError(err, 'Failed to execute proposal.'),
  })

  // --- Reject proposal ---
  const rejectProposal = useMutation({
    mutationFn: () => api.post(`/wizards/sessions/${sessionUuid}/reject/`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wizard-session', sessionUuid] })
    },
    onError: (err) => toastApiError(err, 'Failed to reject proposal.'),
  })

  // --- Abort streaming ---
  const abort = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  return {
    session,
    sessionUuid,
    isLoading: sessionQuery.isLoading,
    isStreaming,
    streamingText,
    isUploading: uploadFile.isPending,
    createSession,
    sendMessage,
    uploadFile: handleUploadFile,
    acceptProposal: acceptProposal.mutateAsync,
    rejectProposal: rejectProposal.mutateAsync,
    isAccepting: acceptProposal.isPending,
    isRejecting: rejectProposal.isPending,
    abort,
  }
}
